import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import useCapacity from "@/lib/useCapacity";
import { EVENT_SOLD_OUT } from "@/lib/tickets";

export default function SocialProof() {
  const { total, tier, vip_remaining, vip_total } = useCapacity();
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const vipTotal = vip_total || 50;
  const vipSold = Math.max(0, vipTotal - (vip_remaining || 0));
  const vipPercent = vipTotal > 0 ? Math.min(100, Math.round((vipSold / vipTotal) * 100)) : 0;
  const gone = EVENT_SOLD_OUT || tier === "sold_out" || vip_remaining <= 0;

  const displayNumber = gone ? (EVENT_SOLD_OUT ? (total || 350) : vipTotal) : vip_remaining;
  const headlineSuffix = EVENT_SOLD_OUT
    ? "билета продадени. Благодарим ви!"
    : gone
      ? "VIP билета са продадени."
      : "VIP билета остават до пълно разпродаване.";

  return (
    <section
      data-testid="social-proof-section"
      className="relative bg-[#0B1F3D] text-[#F5EBD9] py-24 sm:py-32 lg:py-40 overflow-hidden border-t border-[#F5EBD9]/10"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 55% at 50% 50%, rgba(245,137,46,0.24), transparent 65%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(245,235,217,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,235,217,1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-3 mb-8 sm:mb-10"
        >
          <span className="h-px w-10 bg-[#F5892E]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
            Живо преброяване
          </span>
          <span className="h-px w-10 bg-[#F5892E]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 40 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div
            data-testid="social-proof-number"
            className="font-display display-tight text-[38vw] sm:text-[28vw] lg:text-[22vw] xl:text-[19rem] leading-[0.85] text-[#E0B95C]"
            style={{ textShadow: "0 8px 60px rgba(224,185,92,0.35)" }}
          >
            {displayNumber}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-6 sm:mt-8"
        >
          <div className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-[1.1] tracking-tight text-[#F5EBD9]">
            {headlineSuffix}
          </div>
          {!gone && (
            <div className="mt-3 sm:mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5EBD9]/85">
              А ти? <span className="text-[#E0B95C]">Не остават много.</span>
            </div>
          )}
        </motion.div>

        {/* Progress bar — hidden when sold out */}
        {!EVENT_SOLD_OUT && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-10 sm:mt-14 max-w-2xl mx-auto"
          data-testid="capacity-progress"
        >
          <div className="flex items-center justify-between text-xs sm:text-sm font-mono uppercase tracking-[0.24em] text-[#F5EBD9]/70 mb-3">
            <span>
              <span className="text-[#F5EBD9]">{vipSold}</span> / {vipTotal} VIP места заети
            </span>
            <span className="text-[#E0B95C]">{vipPercent}%</span>
          </div>
          <div className="h-2.5 bg-[#0F2647] border border-[#F5EBD9]/10 overflow-hidden rounded-full">
            <motion.div
              initial={{ width: "0%" }}
              whileInView={{ width: `${vipPercent}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
              className="h-full bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C]"
            />
          </div>
        </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-10 sm:mt-12"
        >
          <button
            onClick={() => scrollTo(EVENT_SOLD_OUT ? "notify" : "tickets")}
            data-testid="social-proof-cta"
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 rounded-full uppercase tracking-[0.18em] text-sm sm:text-base font-bold bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] hover:scale-[1.03] transition-transform shadow-[0_15px_40px_-10px_rgba(224,185,92,0.7)]"
          >
            {EVENT_SOLD_OUT ? "Запиши имейла си — 25.10" : "Вземи VIP билет сега"}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.6} />
          </button>
          <div className="mt-6 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/50">
            {EVENT_SOLD_OUT ? (
              <span className="text-[#E5484D]">Всички билети са разпродадени</span>
            ) : (
              <>VIP билет · 119 € · <span className="text-[#E5484D]">Стандартните билети са изчерпани</span></>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
